import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import Image from "next/image";
import { Navigation, Thumbs, Swiper as SwiperInstance, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import UIButton from 'components/Others/UIButton';
import { BackgroundColor, generateGlassesImage, generateGlassesThumbImage, generateWTBImage } from 'utils/image.utils';
import * as ga from 'lib/ga';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import ProductSpecificIcon from 'components/Others/ProductSpecificIcon';
import { useGlassesStore } from 'zus/glassesStore';
import { useVTOStore } from 'zus/vtoStore';
import { useRouter } from 'next/router';
import { imageLoader } from 'functions/imageLoader';
import Spinner from 'components/Spinner/Spinner';
import InstagramVideoModal from 'components/InstagramVideo/InstagramVideoModal';

const VTOModalV2 = dynamic(
  () => import('components/VTO/VTOModalV2'),
  { ssr: false }
);
import { CategoryLensTypes, FrameShape } from 'constants/dataTypes';
import dynamic from 'next/dynamic';
import { slugToString } from 'functions/slug';
import { useQueryState } from 'nuqs';
import InstagramVideoIcon from 'components/Icons/InstagramVideoIcon';
import UITooltip from 'components/Others/UITooltip';

type ImageAspect = '0' | '1' | '2' | '3_model_m' | '3_model_f' | '4' | 'instagram_video';

interface ProductImageSliderProps {
  category: string;
  frameColor: string;
  categoryLensTypes?: CategoryLensTypes[];
  instagramVideos?: InstagramVideo[];
}

interface InstagramVideo {
  id: string;
  videoUrl: string;
  thumbnailUrl: string;
  title?: string;
  description?: string;
  publishedAt?: string;
}

export default function ProductImageSlider({ category, frameColor, categoryLensTypes, instagramVideos = [] }: ProductImageSliderProps) {

  const [swiperInstance, setSwiperInstance] = useState<any | SwiperInstance | null>(
    null
  );
  const router = useRouter();

  const [loading, setLoading] = useState(false);
  const [isInstagramModalOpen, setIsInstagramModalOpen] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [isInstagramVideoActive, setIsInstagramVideoActive] = useState(false);


  const showVTOToggle = true;
  const isSunglasses = category.toLowerCase().includes('sunglasses');


  const [armColorQuery, setArmColorQuery] = useQueryState('armColor');
  const [lensColorQuery, setLensColorQuery] = useQueryState('lensColor');

  const armColor = armColorQuery ?? frameColor;
  const lensColor = lensColorQuery ?? (isSunglasses ? 'grey' : 'clear');
  //This should get category_display but it should not update on category change
  const isArmCustomized = router.query.armColor ? true : false; //useGlassesStore((state) => state.is_arm_customized);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(isArmCustomized ? 2 : 0);

  const frameShape = useGlassesStore((state) => state.frame_shape);

  const isVTOVisible = useVTOStore((state) => state.isVisible);
  const updateVisibility = useVTOStore((state) => state.updateVisibility);

  const setSelectedColors = useVTOStore((state) => state.setSelectedColors);
  const setCategory = useVTOStore((state) => state.setCategory);

  const toggleVTO: () => void = () => {
    ga.sendUICustomEvent({
      eventCategory: 'VTO',
      eventAction: 'VTO Toggle Click',
      eventLabel: 'Product Detail Page'
    });


    setCategory(category);
    setSelectedColors(
      frameColor,
      armColor,
      lensColor,
      frameShape.toLowerCase() as FrameShape
    );

    updateVisibility(true);
  }

  const navigateToCarouselSlide = (index: number) => {
    swiperInstance?.slideTo(index + 1);
    swiperInstance?.update();

    setSelectedImageIndex(index);
  };

  useEffect(() => {

    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lensColorQuery, armColor]);


  useEffect(() => {

    if (isArmCustomized) {
      swiperInstance?.slideTo(4);
      swiperInstance?.update();

      setSelectedImageIndex(3);
    } else {
      swiperInstance?.slideTo(1);
      swiperInstance?.update();

      setSelectedImageIndex(0);
    }
  }, [isArmCustomized, setSelectedImageIndex, swiperInstance]);


  let allAspects: ImageAspect[] = ['0', '3_model_f', '3_model_m', '1', '2'];

  // Only add instagram_video if we have videos
  if (instagramVideos.length > 0) {
    allAspects.push('instagram_video');
  }

  if (((router.asPath.indexOf('reading-glasses') > -1 || router.asPath.indexOf('blue-light-filter-glasses') > -1 || router.asPath.indexOf('sunglasses') > -1))) {
    allAspects = ['0', '3_model_f', '3_model_m', '1', '2', '4'];
    // Only add instagram_video if we have videos
    if (instagramVideos.length > 0) {
      allAspects.push('instagram_video');
    }
  }

  // Ensure packaging image (aspect '4') is NOT shown on prescription-sunglasses pages
  if (router.asPath.indexOf('prescription-sunglasses') > -1) {
    allAspects = allAspects.filter((aspect) => aspect !== '4');
  }

  const setFrameShape = useGlassesStore((state) => state.setFrameShape);

  useEffect(() => {
    const shape = router.query.shape as string;
    if (shape) {
      setFrameShape(shape.charAt(0).toUpperCase() + shape.slice(1) as FrameShape);
    } else {
      setFrameShape('classic');
    }
  }, [router.query.shape, setFrameShape]);

  // Handle clicks on Instagram video pagination dot
  useEffect(() => {
    const handleInstagramDotClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const svg = target.closest('#instagram-video-pagination-dot');
      
      if (svg) {
        // Find the Instagram video slide index
        const instagramVideoIndex = allAspects.findIndex(aspect => aspect === 'instagram_video');
        
        if (instagramVideoIndex !== -1) {
          // Navigate to the Instagram video slide
          swiperInstance?.slideTo(instagramVideoIndex + 1);
          swiperInstance?.update();
          setSelectedImageIndex(instagramVideoIndex);
          
          // Send analytics event
          ga.sendUICustomEvent({
            eventCategory: 'Instagram Videos',
            eventAction: 'Pagination Dot Click',
            eventLabel: 'Mobile Slider'
          });
        }
      }
    };

    // Add event listener to the pagination container
    const paginationContainer = document.querySelector('.custom-pagination');
    if (paginationContainer) {
      paginationContainer.addEventListener('click', handleInstagramDotClick);
    }

    // Cleanup
    return () => {
      if (paginationContainer) {
        paginationContainer.removeEventListener('click', handleInstagramDotClick);
      }
    };
  }, [swiperInstance, allAspects, setSelectedImageIndex]);

  function imageSrcGenerator(
    imageAspect: ImageAspect
  ) {
    if (imageAspect == '4') {
      const lensType = router.asPath.indexOf('reading-glasses') > -1 ? 'reading-glasses' : router.asPath.indexOf('blue-light-filter-glasses') > -1 ? 'blue-light-filter-glasses' : 'sunglasses';
      return generateWTBImage(frameColor, armColor, lensColor, lensType, frameShape);
    }
    if (imageAspect == 'instagram_video') {
      return '/videos/instagram-product-demo.mp4'; // Placeholder video URL
    }
    return generateGlassesImage(
      frameColor,
      armColor,
      imageAspect,
      lensColor.replace('grey-mirror', 'mirror'),
      BackgroundColor.Grey,
      frameShape
    );
  }
  function imageThumbSrcGenerator(
    imageAspect: ImageAspect
  ) {
    if (imageAspect == '4') {
      const lensType = router.asPath.indexOf('reading-glasses') > -1 ? 'reading-glasses' : router.asPath.indexOf('blue-light-filter-glasses') > -1 ? 'blue-light-filter-glasses' : 'sunglasses';
      return generateWTBImage(frameColor, armColor, lensColor, lensType, frameShape);
    }
    if (imageAspect == 'instagram_video') {
      return '/images/instagram-video-thumbnail.jpg'; // Placeholder thumbnail URL
    }
    return generateGlassesThumbImage(frameColor, armColor, imageAspect, lensColor, frameShape);
  }
  let slideIndex = 0;
  return (
    <div className="sticky top-[20px] z-[22] w-full xlx:static">
      <div className="group relative flex h-[420px] w-full ßitems-center justify-center bg-[#f6f8fa] xlx:mx-auto xsmx:h-[290px] xsmx:w-full flex-col">
        <ProductSpecificIcon productTitle={frameColor ?? armColorQuery ?? ''} />

        <Swiper
          modules={[Navigation, Pagination, Thumbs]}
          loop
          className="w-full h-[435px] xsmx:h-[305px]"
          onSwiper={setSwiperInstance}
          pagination={{
            clickable: true,
            dynamicBullets: false,
            el: '.custom-pagination',
            renderBullet: (index, className) => {
              const aspect = allAspects[index];
              const isActive = className.includes('swiper-pagination-bullet-active');

              if (aspect === 'instagram_video') {
                return `<svg id="instagram-video-pagination-dot" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style="cursor: pointer;">
                <path d="M2.50476 12.5684C2.9891 12.7598 3.51192 12.853 4.0116 12.8828C4.17061 13.0836 4.34316 13.2788 4.53113 13.4668C5.75179 14.6873 7.2413 15.2978 8.99988 15.2979C10.7587 15.2979 12.249 14.6862 13.4696 13.4639C13.6571 13.276 13.8285 13.0804 13.9872 12.8799C14.4865 12.8475 14.9914 12.762 15.4891 12.5791C15.149 13.1964 14.7344 13.7507 14.244 14.2412C13.577 14.9083 12.7923 15.4371 11.8905 15.8262C10.9888 16.2152 10.0251 16.4092 8.99988 16.4092C7.97464 16.4092 7.01098 16.2153 6.10925 15.8262C5.20749 15.4371 4.42279 14.9083 3.75574 14.2412C3.26251 13.7479 2.84601 13.19 2.50476 12.5684ZM8.99988 1.58594C10.0252 1.58594 10.9887 1.7808 11.8905 2.16992C12.7923 2.55904 13.577 3.08685 14.244 3.75391C14.911 4.42094 15.4389 5.2057 15.828 6.10742C15.8954 6.26355 15.9549 6.4222 16.0106 6.58203C14.8048 6.41921 13.592 6.44129 12.3895 6.59473C11.811 6.66859 11.2375 6.77834 10.6718 6.92188C10.4229 6.83526 10.1765 6.73799 9.93445 6.62598C8.86681 6.1319 7.95646 5.42764 7.203 4.51367C6.87317 5.29945 6.42848 6.00516 5.8739 6.63477C5.86088 6.63286 5.84788 6.63077 5.83484 6.62891V6.62793C5.36309 6.55842 4.89682 6.51385 4.4325 6.49219C4.5828 6.35374 4.73791 6.20459 4.89539 6.04199C5.65506 5.25761 6.19628 4.30995 6.51746 3.19824C5.44276 3.68 4.63064 4.29465 4.08093 5.04199C3.75039 5.49139 3.47253 5.97314 3.24695 6.4873C2.82817 6.50333 2.40854 6.53833 1.98523 6.59082C2.04175 6.42791 2.10312 6.26649 2.17175 6.10742C2.56082 5.20577 3.08879 4.42091 3.75574 3.75391C4.42273 3.08691 5.2076 2.55903 6.10925 2.16992C7.01098 1.78082 7.97464 1.58596 8.99988 1.58594ZM8.99988 2.69727C8.6664 2.69727 8.35099 2.72208 8.05457 2.77148C7.75848 2.82084 7.5056 2.87679 7.29578 2.93848C8.38276 4.21072 9.38628 5.08138 10.3065 5.55078C11.2268 6.02018 12.2364 6.25487 13.3358 6.25488C13.6322 6.25488 13.867 6.24965 14.0399 6.2373C14.2128 6.22496 14.4043 6.18768 14.6141 6.12598C14.0583 5.12539 13.302 4.30349 12.3446 3.66113C11.3873 3.01879 10.2722 2.69727 8.99988 2.69727Z" fill="#CACACA" />
                <path d="M5.75787 7.1537C6.54599 7.26597 7.31626 7.46074 8.08126 7.68311C8.75392 7.87864 9.42187 7.84464 10.0897 7.63685C10.8635 7.39598 11.6554 7.22346 12.4571 7.12114C14.0709 6.91522 15.6858 6.95534 17.2675 7.36952C17.3727 7.39707 17.4291 7.44376 17.4354 7.5569C17.4576 7.9488 17.4644 8.33546 17.4705 8.72776C17.4721 8.82849 17.4426 8.88699 17.3508 8.94148C17.27 8.99443 17.209 9.07433 17.1776 9.16807C16.9647 9.83854 16.8472 10.5454 16.4953 11.1555C16.2773 11.5333 15.9674 11.7957 15.5822 11.9711C14.8419 12.3081 14.0609 12.3866 13.2651 12.3674C12.3465 12.3513 11.3158 12.3177 10.753 11.4963C10.3245 10.8707 10.0764 10.1467 9.79529 9.44131C9.67358 9.13583 9.44729 8.98378 9.1357 8.96252C9.03961 8.95462 8.94306 8.95487 8.84701 8.96325C8.56194 8.99237 8.34193 9.12502 8.22431 9.40948C7.91254 10.1633 7.64753 10.9686 7.15263 11.627C6.51757 12.4719 5.08743 12.3969 4.14971 12.363C2.90961 12.3122 1.69125 11.8791 1.25851 10.6203C1.09331 10.1398 1.04519 9.57192 0.806402 9.12112C0.764368 9.04327 0.704768 8.97721 0.632732 8.92862C0.565473 8.8843 0.527524 8.83442 0.529492 8.75878C0.540243 8.34462 0.554349 7.93052 0.568062 7.51645C0.571335 7.41796 0.642075 7.39386 0.717392 7.3781C1.11304 7.29527 1.50578 7.18924 1.90505 7.13585C3.20296 6.96233 4.4585 6.96222 5.75787 7.1537Z" fill="#CACACA" />
              </svg>`;
              }

              return `<span class="${className}" style="width: 6px; height: 6px; background: ${isActive ? '#FF8200' : '#CACACA'}; border-radius: 50%; margin: 0 2.5px;"></span>`;
            }
          }}
          onSlideChange={(swiper) => {
            const currentAspect = allAspects[swiper.realIndex];

            // Check if we're on desktop and reached Instagram video slide
            if (currentAspect === 'instagram_video' && window.innerWidth >= 1024) {
              // Skip Instagram video on desktop - go back to start
              setTimeout(() => {
                swiper.slideTo(1);
              }, 100);
              return;
            }

            setSelectedImageIndex(swiper.realIndex);

            // Update Instagram video pagination dot color using ID
            const instagramSvg = document.getElementById('instagram-video-pagination-dot');
            if (instagramSvg) {
              const paths = instagramSvg.querySelectorAll('path');
              const fillColor = swiper.realIndex === allAspects.length - 1 ? '#FF8200' : '#CACACA';
              if (swiper.realIndex === allAspects.length - 1) {
                setIsInstagramVideoActive(true);
              } else {
                setIsInstagramVideoActive(false);
              }
              paths.forEach(path => {
                path.setAttribute('fill', fillColor);
              });
            }
          }}
          slidesPerView={1}>
          {allAspects.map((aspect, index) => (
            <SwiperSlide
              className={cx("relative flex !h-[420px] w-full rounded border-2 border-brand-grey125 xsmx:!h-[290px]", {
                'bg-[#d4d4d4]': aspect === '4'
              })}
              key={slideIndex++}>
              {loading && aspect !== 'instagram_video' && (<div className="mr-2 flex justify-center items-center pt-[1px] h-full w-full">
                <Spinner fill="grey" />
              </div>)}

              {aspect === 'instagram_video' ? (
                <div className="w-full h-full flex flex-col items-center justify-center bg-white p-6 border border-[#EAEAEA] rounded">
                  {/* Mobile version - show 6 thumbnails + button */}
                  <div className="lg:hidden w-full h-full flex flex-col items-center justify-center p-4">
                    {instagramVideos.length > 0 ? (
                      <>
                        {/* 2x3 Grid of thumbnails */}
                        <div className="grid grid-cols-3 gap-3 mb-4 w-full max-w-[297px]">
                          {instagramVideos.slice(0, 3).map((video, videoIndex) => (
                            <button
                              key={video.id}
                              onClick={() => {
                                // Mobile individual thumbnail click event
                                ga.sendUICustomEvent({
                                  eventCategory: 'Instagram Videos',
                                  eventAction: 'Individual Thumbnail Click',
                                  eventLabel: `Mobile Slider - Video ${videoIndex + 1}`
                                });
                                setIsInstagramModalOpen(true);
                                // Set initial video index to the clicked thumbnail
                                setSelectedVideoIndex(videoIndex);
                              }}
                              className="aspect-square rounded-[12px] overflow-hidden bg-gray-100 hover:opacity-80 transition-opacity"
                            >
                              <Image
                                src={video.thumbnailUrl}
                                alt={`Customer testimonial ${videoIndex + 1}`}
                                width={91}
                                height={91}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>

                        {/* See All Button */}
                        <button
                          onClick={() => {
                            // Mobile See All Customer Testimonials button click event
                            ga.sendUICustomEvent({
                              eventCategory: 'Instagram Videos',
                              eventAction: 'See All Customer Testimonials Click',
                              eventLabel: 'Mobile Slider'
                            });
                            setIsInstagramModalOpen(true);
                          }}
                          className="bg-[#2A2A2A] text-white px-[18px] py-[10px] rounded-full text-xs font-medium hover:bg-gray-800 transition-colors"
                        >
                          See All Customer Testimonials
                        </button>
                      </>
                    ) : (
                      // Fallback when no videos
                      <div
                        className="flex flex-col items-center justify-center cursor-pointer"
                        onClick={() => {
                          // Mobile fallback click event (when no videos available)
                          ga.sendUICustomEvent({
                            eventCategory: 'Instagram Videos',
                            eventAction: 'Fallback Click',
                            eventLabel: 'Mobile Slider - No Videos'
                          });
                          setIsInstagramModalOpen(true);
                        }}
                      >
                        <div className="w-16 h-16 bg-white rounded-lg shadow-md flex items-center justify-center mb-4">
                          <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0)">
                              <path fillRule="evenodd" clipRule="evenodd" d="M7.75 7C7.75 7 7 7.75 7 7.75V35C7 35 7.75 35.75 7.75 35.75H26.5C27.25 35.75 28 35 28 35V28H35C35 28 35.75 27.25 35.75 26.5V7C35.75 7 35 6.25 34.25 6.25H7.75V7Z" fill="#616161" />
                              <path d="M5 17.22H37V27.38H5V17.22Z" fill="#616161" />
                              <path d="M31 0H42V9.33H31V0Z" fill="#616161" />
                            </g>
                            <defs>
                              <clipPath id="clip0">
                                <rect width="42" height="42" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                        <span className="text-sm text-gray-600 font-medium text-center">View Instagram Videos</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <Image
                  quality={92}
                  key={`img${category}${frameColor}${armColorQuery}${lensColor}${frameShape}${index}`}
                  fill
                  style={{
                    objectFit: aspect !== '4' ? 'cover' : 'contain',
                    objectPosition: (index === 1 || index === 2) ? 'center 30%' : 'center center'
                  }}
                  sizes="540px"
                  loader={imageLoader}
                  priority

                  onLoadingComplete={(image) => {
                    if (index == 0) {
                      setLoading(false);
                    }
                    image.classList.remove("opacity-0");
                  }}
                  className={cx("transition-opacity opacity-0 duration-[0.5s]", {
                    'scale-100': aspect === '4'
                  })}
                  src={imageSrcGenerator(aspect)}
                  alt={`${category} ${frameColor} ${frameShape}`}
                />
              )}

            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom pagination for mobile */}
        <div className="custom-pagination lg:hidden !absolute !bottom-[-20px] !left-1/2 !transform !-translate-x-1/2 flex justify-center items-center gap-1 !z-99" style={{ bottom: '-20px' }}></div>

        {!!showVTOToggle && !isInstagramVideoActive && (
          <div className="absolute bottom-[10px] left-[50%] z-[30] translate-x-[-50%]">
            <UIButton rounded={true} onClick={toggleVTO}>
              Virtual Try On
            </UIButton>
          </div>
        )}
        {isVTOVisible && (
          <VTOModalV2
            categoryLensTypes={categoryLensTypes}
            title={slugToString(category)}
          />
        )}
      </div>

      <div className="gap-2 hidden lg:flex pt-4">
        {allAspects.map((aspect, index) => (

          <button
            onClick={() => {
              if (aspect === 'instagram_video') {
                // Desktop Customer Testimonial button click event
                ga.sendUICustomEvent({
                  eventCategory: 'Instagram Videos',
                  eventAction: 'Customer Testimonial Click',
                  eventLabel: 'Desktop Thumbnail'
                });
                setIsInstagramModalOpen(true);
              } else {
                navigateToCarouselSlide(index);
              }
            }}
            key={index}
            className={cx(
              'relative h-[60px] w-[90px] rounded border-brand-grey125 bg-white',

              {
                'border border-[#616161] border-solid rounded-md': selectedImageIndex === index,
                'bg-[#d4d4d4]': aspect === '4'
              }
            )}>

            {aspect === 'instagram_video' ? (
              <UITooltip
                id="instagram-video-tooltip"
                place='top'
                messageWrapperClassName='whitespace-pre text-[#2A2A2A] h-[25px]'
                message={'Customer Testimonials'}
                borderColour={'#E2E2E2'}
                textColour={'#000'}
                backgroundColour={'#FFF'}
                border={true}
                tooltipWrapperClassName='!rounded-[4px] border !border-[#E2E2E2] border-solid !bg-white w-[200px] text-center'
                wrapperClassName="w-[60px] h-[100%]"
              >
                <div className="relative w-[60px] h-full rounded-md overflow-hidden bg-[#F7F8FA] flex items-center justify-center cursor-pointer">
                  {/* Photo merge icon from Figma */}
                  <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.73242 27.749C9.64638 28.1098 10.6344 28.2807 11.5771 28.3369C11.8778 28.7165 12.2032 29.086 12.5586 29.4414C14.8644 31.747 17.678 32.9004 21 32.9004C24.3222 32.9004 27.1358 31.7454 29.4414 29.4365C29.7962 29.0812 30.1207 28.7115 30.4209 28.332C31.3643 28.2708 32.3184 28.1095 33.2588 27.7637C32.616 28.9306 31.8322 29.9783 30.9053 30.9053C29.6453 32.1653 28.1633 33.1625 26.46 33.8975C24.7566 34.6325 22.9367 35 21 35C19.0633 35 17.2434 34.6325 15.54 33.8975C13.8367 33.1625 12.3547 32.1653 11.0947 30.9053C10.1638 29.9743 9.37677 28.922 8.73242 27.749ZM21 7C22.9367 7 24.7566 7.36754 26.46 8.10254C28.1633 8.83754 29.6453 9.83473 30.9053 11.0947C32.1653 12.3547 33.1625 13.8367 33.8975 15.54C34.0249 15.8353 34.1408 16.1342 34.2461 16.4365C31.9672 16.1284 29.6759 16.17 27.4033 16.46C26.3078 16.5998 25.2217 16.807 24.1504 17.0791C23.6826 16.916 23.2208 16.7301 22.7656 16.5195C20.7488 15.5863 19.0287 14.2567 17.6055 12.5303C16.9829 14.0134 16.1461 15.3475 15.0996 16.5361C15.0732 16.5323 15.0469 16.5282 15.0205 16.5244L15.0215 16.5234C14.1312 16.3923 13.2513 16.3076 12.375 16.2666C12.6582 16.0056 12.9504 15.7243 13.2471 15.418C14.6821 13.9363 15.7029 12.1449 16.3096 10.0449C14.2797 10.9549 12.7453 12.1158 11.707 13.5273C11.0828 14.376 10.5598 15.2869 10.1338 16.2578C9.34199 16.2881 8.54843 16.3529 7.74805 16.4521C7.85481 16.1445 7.97292 15.8404 8.10254 15.54C8.83754 13.8367 9.83473 12.3547 11.0947 11.0947C12.3547 9.83473 13.8367 8.83754 15.54 8.10254C17.2434 7.36754 19.0633 7 21 7ZM21 9.09961C20.37 9.09961 19.7748 9.1469 19.2148 9.24023C18.6551 9.33353 18.1768 9.43807 17.7803 9.55469C19.8336 11.958 21.7294 13.6036 23.4678 14.4902C25.206 15.3768 27.1139 15.8203 29.1904 15.8203C29.75 15.8203 30.193 15.8085 30.5195 15.7852C30.8462 15.7618 31.2088 15.6919 31.6055 15.5752C30.5555 13.6852 29.1257 12.1333 27.3174 10.9199C25.5091 9.70664 23.4033 9.09961 21 9.09961Z" fill="#616161" />
                    <path d="M14.876 17.513C16.3646 17.7251 17.8196 18.0929 19.2646 18.513C20.5352 18.8823 21.7969 18.8181 23.0583 18.4256C24.5199 17.9706 26.0158 17.6447 27.5301 17.4515C30.5783 17.0625 33.6287 17.1383 36.6164 17.9206C36.8151 17.9727 36.9216 18.0609 36.9336 18.2746C36.9755 19.0148 36.9882 19.7452 36.9999 20.4862C37.0028 20.6765 36.9471 20.787 36.7737 20.8899C36.6212 20.9899 36.5059 21.1408 36.4466 21.3179C36.0444 22.5843 35.8225 23.9195 35.1577 25.0718C34.746 25.7856 34.1607 26.2813 33.4331 26.6125C32.0348 27.2491 30.5596 27.3974 29.0564 27.361C27.3212 27.3306 25.3743 27.2672 24.3113 25.7156C23.5018 24.534 23.0331 23.1664 22.5022 21.834C22.2723 21.257 21.8449 20.9698 21.2563 20.9296C21.0748 20.9147 20.8924 20.9152 20.711 20.931C20.1725 20.986 19.757 21.2366 19.5348 21.7739C18.9459 23.1978 18.4453 24.7189 17.5105 25.9626C16.3109 27.5585 13.6096 27.4169 11.8383 27.3527C9.49591 27.2569 7.19457 26.4387 6.37718 24.061C6.06513 23.1533 5.97423 22.0807 5.52319 21.2292C5.44379 21.0822 5.33122 20.9574 5.19515 20.8656C5.0681 20.7819 4.99642 20.6877 5.00014 20.5448C5.02044 19.7625 5.04709 18.9803 5.07299 18.1982C5.07917 18.0121 5.21279 17.9666 5.35506 17.9368C6.10239 17.7804 6.84424 17.5801 7.59842 17.4793C10.05 17.1515 12.4216 17.1513 14.876 17.513Z" fill="#616161" />
                    <path d="M36.5 1.39333C36.1333 0.928889 35.7544 0.580555 35.3633 0.348333C34.9722 0.116111 34.4711 0 33.86 0C33.08 0 32.4084 0.281722 31.8452 0.845166C31.2817 1.40837 31 2.07998 31 2.86C31 4.11987 31.5674 5.2712 32.7021 6.314C33.837 7.35655 34.8264 8.25085 35.6702 8.9969C35.9022 9.22179 36.1806 9.33423 36.5055 9.33423C36.8301 9.33423 37.1049 9.22179 37.3298 8.9969C38.1736 8.25085 39.163 7.34885 40.2979 6.2909C41.4326 5.23294 42 4.09053 42 2.86367C42 2.0812 41.7183 1.40837 41.1548 0.845166C40.5916 0.281722 39.92 0 39.14 0C38.5289 0 38.0278 0.116111 37.6367 0.348333C37.2456 0.580555 36.8667 0.928889 36.5 1.39333Z" fill="#616161" />
                  </svg>
                </div>
              </UITooltip>
            ) : (
              <Image
                quality={80}
                fill
                key={`thumb${category}${frameColor}${armColorQuery}${lensColor}${frameShape}${index}`}
                style={{
                  objectFit: aspect !== '4' ? 'cover' : 'contain',
                  objectPosition: (index === 1 || index === 2) ? 'center 30%' : 'center center'
                }}
                onLoadingComplete={(image) => {
                  image.classList.remove("opacity-0");
                }}
                loader={imageLoader}
                sizes="88px"
                src={imageThumbSrcGenerator(aspect)}
                alt={`${category} ${frameColor}`}
                className={`rounded-md transition-opacity opacity-0 duration-[0.1s]`}
                priority={true}
              />
            )}

          </button>
        ))}
      </div>

      {/* Instagram Video Modal */}
      < InstagramVideoModal
        isOpen={isInstagramModalOpen}
        onClose={() => setIsInstagramModalOpen(false)}
        initialVideoIndex={selectedVideoIndex}
        videos={instagramVideos}
      />
    </div >
  );
}
